import React, { useEffect, useRef, useState } from 'react';
import { feedbackExtras, reviewSiteScores } from '../services/api';

// The live admin portal's dashboard Feedback card, rebuilt for CPx (Greg, Oct 20, 2026).
// Same functionality as crab-place-x-admin-front-end src/app/dashboard/feedback: the send's
// scores, Product broken out by category (Crab Legs split into King and Snow), the reviews
// that scored the product below 4, photo entries and customers sent to review sites since
// the send. Every figure comes from feedback-extras.js, the function the portal calls, so
// the two screens cannot disagree. Opens as a bottom sheet on a phone, a centred panel on
// a desktop.

// The send day as YYYY-MM-DD. `ymd` is set by fetchFeedbackData; an older snapshot only
// carries the display date (M/D/YY), so read that as a fallback.
export function sendYmd(feedbackData) {
  if (!feedbackData) return '';
  if (feedbackData.ymd) return feedbackData.ymd;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(feedbackData.date || '');
  if (!m) return '';
  const y = m[3].length === 2 ? `20${m[3]}` : m[3];
  return `${y}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function sendLabel(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : '';
}
const when = (iso, withTime) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return '';
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' });
  return withTime ? `${day}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}` : day;
};

// The portal's colour rule: under 4 is a problem, under 4.5 is worth a look.
const scoreColor = (v) => (v == null ? 'var(--gray-700)' : v < 4 ? 'var(--red)' : v < 4.5 ? 'var(--orange)' : 'var(--green)');
const fmt = (v, dp) => (v == null || isNaN(v) ? '–' : Number(v).toFixed(dp));

const S = {
  muted: { fontSize: 11, color: 'var(--gray-500)' },
  msg: { padding: '20px 16px', fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' },
  err: { padding: '12px 16px', fontSize: 12, color: 'var(--red)' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid var(--gray-200, #e6e8ec)' },
  link: { background: 'none', border: 'none', padding: 0, color: 'var(--orange)', fontWeight: 700, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  quote: { fontSize: 12, color: 'var(--gray-700)', fontStyle: 'italic', marginTop: 4, lineHeight: 1.45 },
};

function useLoad(fn, deps) {
  const [state, set] = useState({ loading: true, error: '', data: null });
  useEffect(() => {
    let live = true;
    set({ loading: true, error: '', data: null });
    fn().then((data) => live && set({ loading: false, error: '', data }))
      .catch((e) => live && set({ loading: false, error: e.message, data: null }));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

function Scores({ feedbackData, extras, go }) {
  const scores = feedbackData?.scores || [];
  return (
    <div style={{ padding: '0 16px 16px' }}>
      {scores.map((s) => {
        const isProduct = s.label === 'Product';
        const v = Number(s.score);
        return (
          <div key={s.label} style={{ ...S.row, cursor: isProduct ? 'pointer' : 'default' }} onClick={isProduct ? () => go('category') : undefined}>
            <div style={{ fontSize: 14, fontWeight: s.label === 'Overall' ? 800 : 600, color: 'var(--navy)' }}>
              {s.label}
              {isProduct && <span style={{ ...S.muted, color: 'var(--orange)', fontWeight: 700, marginLeft: 8 }}>By Category ›</span>}
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: scoreColor(v) }}>★ {s.score}</div>
          </div>
        );
      })}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
        {[['photos', 'Photo Entries', extras?.photos], ['reviews', 'Sent to Review Sites', extras?.review_sites]].map(([k, label, n]) => (
          <button key={k} type="button" onClick={() => go(k)} style={{ textAlign: 'left', border: '1px solid var(--gray-200, #e6e8ec)', borderRadius: 10, padding: '10px 12px', background: 'var(--peach-light, #fff8ee)', cursor: 'pointer', fontFamily: 'inherit' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', lineHeight: 1 }}>{n ?? '–'}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--orange)', marginTop: 4 }}>{label} ›</div>
          </button>
        ))}
      </div>
      <div style={{ ...S.muted, marginTop: 8 }}>
        Since this send went out. A review-site visit means they opened Google, Facebook or Trustpilot from our thank-you page; we cannot see whether they posted.
      </div>
    </div>
  );
}

function ByCategory({ ymd, go }) {
  const { loading, error, data } = useLoad(() => feedbackExtras(ymd, { categories: 1, full: 1 }), [ymd]);
  if (loading) return <div style={S.msg}>Loading…</div>;
  if (error) return <div style={S.err}>⚠ {error}</div>;
  const rows = data?.rows || [];
  const low = data?.low || {};
  if (!rows.length) return <div style={S.msg}>No feedback with products for this send yet.</div>;
  const th = { fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px', padding: '6px 4px', textAlign: 'right' };
  const td = { fontSize: 13, padding: '9px 4px', textAlign: 'right', borderBottom: '1px solid var(--gray-200, #e6e8ec)' };
  return (
    <div style={{ padding: '0 16px 16px' }}>
      <div style={{ ...S.muted, marginBottom: 6 }}>An order with products from two categories counts in both. Tap a Below 4 count to read those reviews.</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Category</th>
            <th style={th}>Product</th>
            <th style={th}>Overall</th>
            <th style={th}>Reviews</th>
            <th style={th} title="Reviews that scored the product 1, 2 or 3">Below 4</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.key}>
              <td style={{ ...td, textAlign: 'left', fontWeight: c.sub ? 500 : 700, color: 'var(--navy)', paddingLeft: c.sub ? 16 : 4 }}>{c.sub ? '↳ ' : ''}{c.name}</td>
              <td style={{ ...td, fontWeight: 800, color: scoreColor(c.product) }}>{fmt(c.product, 2)}</td>
              <td style={td}>{fmt(c.overall, 1)}</td>
              <td style={td}>{c.reviews}</td>
              <td style={td}>
                {low[c.key] ? <button type="button" style={S.link} onClick={() => go('low', { key: c.key, name: c.name })}>{low[c.key]} ›</button> : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LowReviews({ ymd, cat }) {
  const { loading, error, data } = useLoad(() => feedbackExtras(ymd, { detail: 'low', category: cat.key }), [ymd, cat.key]);
  if (loading) return <div style={S.msg}>Loading…</div>;
  if (error) return <div style={S.err}>⚠ {error}</div>;
  const reviews = data?.reviews || [];
  if (!reviews.length) return <div style={S.msg}>No product scores below 4 here.</div>;
  return (
    <div style={{ padding: '0 16px 16px' }}>
      <div style={{ ...S.muted, marginBottom: 4 }}>Lowest product score first.</div>
      {reviews.map((r, i) => (
        <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--gray-200, #e6e8ec)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
            {r.customer || 'Unknown customer'} <span style={{ ...S.muted, fontWeight: 500 }}>· order {r.order}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-700)', marginTop: 3 }}>
            Product <b style={{ color: scoreColor(r.product) }}>{r.product}</b> · Packaging {r.packaging ?? '–'} · Delivery {r.delivery ?? '–'} · Overall {r.overall ?? '–'}
          </div>
          {r.items?.length > 0 && <div style={{ ...S.muted, marginTop: 3 }}>{r.items.join(', ')}</div>}
          {r.comment ? <div style={S.quote}>“{r.comment}”</div> : <div style={{ ...S.muted, marginTop: 4 }}>No comment left.</div>}
        </div>
      ))}
    </div>
  );
}

function Photos({ ymd }) {
  const { loading, error, data } = useLoad(() => feedbackExtras(ymd, { detail: 'photos' }), [ymd]);
  if (loading) return <div style={S.msg}>Loading…</div>;
  if (error) return <div style={S.err}>⚠ {error}</div>;
  const entries = data?.entries || [];
  if (!entries.length) return <div style={S.msg}>No photo entries since this send.</div>;
  return (
    <div style={{ padding: '0 16px 16px' }}>
      {entries.map((e) => (
        <div key={e.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--gray-200, #e6e8ec)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
            {e.name || 'No name given'}{e.location ? ` · ${e.location}` : ''}
            {e.status === 'winner' && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, background: 'var(--orange)', color: '#fff', borderRadius: 8, padding: '2px 8px' }}>Winner</span>}
          </div>
          <div style={{ ...S.muted, marginTop: 2 }}>#{e.id} · {e.source}{e.order ? ` · order ${e.order}` : ''} · {when(e.created_at)}</div>
          {e.story && <div style={S.quote}>“{e.story}”</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {(e.photos || []).map((p, i) => (
              <a key={i} href={e.photos.length > 1 ? e.all.replace('#p1', `#p${i + 1}`) : p} target="_blank" rel="noopener noreferrer">
                <img src={p} loading="lazy" alt={`Submission ${i + 1} from ${e.name || 'a customer'}`}
                  style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--gray-200, #e6e8ec)' }} />
              </a>
            ))}
          </div>
        </div>
      ))}
      <div style={{ ...S.muted, marginTop: 10 }}>Pick winners in Marketing &gt; Customer Photos.</div>
    </div>
  );
}

const SITE_ORDER = ['Google', 'Facebook', 'Trustpilot'];
function groupBySite(customers) {
  const groups = new Map();
  customers.forEach((c) => {
    const sites = (c.sites || []).length ? c.sites : ['Other'];
    sites.forEach((site) => {
      if (!groups.has(site)) groups.set(site, []);
      groups.get(site).push(c);
    });
  });
  // Every site gets a header even with nobody sent there, so its score is always on show
  // (Greg, Oct 21, 2026: "show the header for the ones, even if they don't have a review").
  SITE_ORDER.forEach((site) => { if (!groups.has(site)) groups.set(site, []); });
  const rank = (k) => { const i = SITE_ORDER.indexOf(k); return i === -1 ? SITE_ORDER.length : i; };
  return [...groups.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || b[1].length - a[1].length);
}

// Average of one score across a list, skipping customers with no score. null when none have one.
function avgScore(list, key) {
  const v = list.map((c) => c[key]).filter((x) => typeof x === 'number');
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
}

// "★ 4.4 · 546 reviews  +0.1 since Sep 21" for one site's header. Trustpilot's number is
// its TrustScore, which is what the site itself shows.
function SiteScore({ score }) {
  if (!score) return null;
  if (score.rating == null) return <span style={{ ...S.muted, marginLeft: 8 }}>score unavailable</span>;
  const p = score.previous;
  const d = p && p.rating != null ? Math.round((score.rating - p.rating) * 100) / 100 : null;
  const dn = p && p.count != null && score.count != null ? score.count - p.count : null;
  const since = p ? new Date(`${String(p.day).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  return (
    <a href={score.link} target="_blank" rel="noopener noreferrer"
      title={`Our current ${score.site === 'trustpilot' ? 'TrustScore' : 'rating'}${score.as_of ? `, checked ${new Date(score.as_of).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}. Opens the site.`}
      style={{ marginLeft: 10, fontSize: 13, fontWeight: 700, color: 'var(--navy)', textDecoration: 'none' }}>
      <span style={{ color: 'var(--orange, #dc582a)' }}>★</span> {String(Number(score.rating.toFixed(2)))}
      <span style={{ fontWeight: 500, color: 'var(--gray-700)', fontSize: 12 }}> · {(score.count ?? 0).toLocaleString()} reviews</span>
      {p && (d !== 0 || dn) ? (
        <span style={{ fontWeight: 600, fontSize: 11, marginLeft: 6, color: d > 0 ? '#1f7a3a' : d < 0 ? '#b42318' : 'var(--gray-500, #86868b)' }}>
          {d ? `${d > 0 ? '+' : ''}${d.toFixed(2)}` : ''}{d && dn ? ', ' : ''}{dn ? `${dn > 0 ? '+' : ''}${dn} reviews` : ''} since {since}
        </span>
      ) : null}
    </a>
  );
}

function ReviewSites({ ymd }) {
  const { loading, error, data } = useLoad(() => feedbackExtras(ymd, { detail: 'reviews' }), [ymd]);
  const scores = useLoad(() => reviewSiteScores(), []);
  if (loading) return <div style={S.msg}>Loading…</div>;
  if (error) return <div style={S.err}>⚠ {error}</div>;
  const customers = data?.customers || [];
  const scoreBy = Object.fromEntries((scores.data || []).map((s) => [s.site, s]));
  // A customer who opened two sites is listed under both, so the list runs longer than the
  // customer count. Say so, or 14 customers reads as a sync miss next to 15 entries.
  const visits = customers.reduce((n, c) => n + Math.max(1, (c.sites || []).length), 0);
  const multi = customers.filter((c) => (c.sites || []).length > 1).length;
  return (
    <div style={{ padding: '0 16px 16px' }}>
      <div style={{ ...S.muted, marginBottom: 4 }}>Customers who opened Google, Facebook or Trustpilot from the thank-you page after leaving feedback. We cannot see whether they posted.</div>
      {!customers.length && <div style={{ ...S.msg, paddingBottom: 0 }}>Nobody has gone to a review site since this send.</div>}
      {customers.length > 0 && (
        <div style={{ display: 'flex', gap: 18, marginTop: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--gray-50, #f6f7f9)', fontSize: 13, color: 'var(--navy)' }}>
          <span>
            <b>{customers.length}</b> {customers.length === 1 ? 'customer' : 'customers'}
            {visits > customers.length && (
              <span style={{ color: 'var(--gray-700)', fontWeight: 400 }}>, {visits} site visits ({multi} went to {multi === 1 ? 'two sites' : 'more than one site'})</span>
            )}
          </span>
          {avgScore(customers, 'overall') != null && <span>Overall <span style={{ color: 'var(--orange, #dc582a)' }}>★</span> <b>{avgScore(customers, 'overall').toFixed(2)}</b> avg</span>}
          {avgScore(customers, 'product') != null && <span>Product <span style={{ color: 'var(--orange, #dc582a)' }}>★</span> <b>{avgScore(customers, 'product').toFixed(2)}</b> avg</span>}
        </div>
      )}
      {/* One header per site (Greg, Oct 20, 2026), so who went where reads at a glance. A
          customer who opened two sites shows under both. */}
      {groupBySite(customers).map(([site, list]) => (
        <div key={site}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 14, padding: '6px 0', borderBottom: '2px solid var(--orange, #dc582a)' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--navy)' }}>
              {site}
              <SiteScore score={scoreBy[site.toLowerCase()]} />
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: list.length ? 'var(--orange, #dc582a)' : 'var(--gray-500, #86868b)', whiteSpace: 'nowrap' }}>
              {list.length ? `${list.length} sent` : 'None sent'}
              {avgScore(list, 'overall') != null && ` · ★ ${avgScore(list, 'overall').toFixed(2)} avg`}
            </div>
          </div>
          {list.map((c, i) => (
            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--gray-200, #e6e8ec)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{c.customer || 'Unknown'}</div>
                <div style={S.muted}>{when(c.tapped_at, true)}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray-700)', marginTop: 3 }}>
                {(c.sites || []).length > 1 && <>Also went to <b>{c.sites.filter((x) => x !== site).join(', ')}</b> · </>}
                {c.order ? `Order ${c.order} · ` : ''}
                Product <b style={{ color: scoreColor(c.product) }}>{c.product ?? '–'}</b> · Overall {c.overall ?? '–'}
              </div>
              {c.comment && <div style={S.quote}>“{c.comment}”</div>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function FeedbackSheet({ feedbackData, extras, initialView = 'scores', onClose }) {
  const ymd = sendYmd(feedbackData);
  const [stack, setStack] = useState([{ view: initialView }]);
  const top = stack[stack.length - 1];
  const go = (view, cat) => setStack((s) => [...s, { view, cat }]);
  const back = () => setStack((s) => s.slice(0, -1));
  const sent = sendLabel(ymd);

  // A ref, so the page re-rendering with a new onClose does not re-run this effect.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, []);

  const titles = {
    scores: 'Feedback',
    category: 'Product Score by Category',
    low: `Below 4: ${top.cat?.name || ''}`,
    photos: 'Photo Entries',
    reviews: 'Sent to Review Sites',
  };
  const wide = typeof window !== 'undefined' && window.innerWidth >= 768;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: wide ? 'center' : 'flex-end', justifyContent: 'center',
      animation: 'ga4DrillFadeIn 180ms ease-out',
    }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label={titles[top.view]} style={{
        width: '100%', maxWidth: wide ? 640 : '100%', background: '#fff',
        borderRadius: wide ? 16 : '16px 16px 0 0', maxHeight: wide ? '82vh' : '85vh', overflowY: 'auto',
        boxShadow: '0 -8px 24px rgba(0,0,0,0.18)', animation: wide ? 'none' : 'ga4DrillSlideUp 220ms ease-out',
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
      }}>
        {!wide && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 2px' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--gray-300, #d4d8de)' }} />
          </div>
        )}
        <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1, display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 16px 12px', borderBottom: '1px solid var(--gray-200, #e6e8ec)' }}>
          {stack.length > 1 && (
            <button type="button" onClick={back} aria-label="Back" style={{ border: 'none', background: 'var(--gray-200)', borderRadius: 8, width: 30, height: 30, fontSize: 16, fontWeight: 700, color: 'var(--navy)', cursor: 'pointer', flex: 'none' }}>‹</button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>{titles[top.view]}</div>
            <div style={{ ...S.muted, marginTop: 2 }}>
              {top.view === 'scores'
                ? `${feedbackData?.count ?? 0} reviews · feedback sent ${sent || '—'}`
                : `${top.view === 'photos' || top.view === 'reviews' ? 'Since' : 'Feedback sent'} ${sent || '—'}`}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'transparent', fontSize: 24, lineHeight: 1, color: 'var(--gray-500)', cursor: 'pointer', padding: 2 }}>×</button>
        </div>
        <div style={{ paddingTop: 4 }}>
          {!ymd && top.view !== 'scores' && <div style={S.msg}>There is no feedback send to show yet.</div>}
          {top.view === 'scores' && <Scores feedbackData={feedbackData} extras={extras} go={go} />}
          {ymd && top.view === 'category' && <ByCategory ymd={ymd} go={go} />}
          {ymd && top.view === 'low' && <LowReviews ymd={ymd} cat={top.cat} />}
          {ymd && top.view === 'photos' && <Photos ymd={ymd} />}
          {ymd && top.view === 'reviews' && <ReviewSites ymd={ymd} />}
        </div>
      </div>
    </div>
  );
}
